import React, { useState, useEffect } from 'react';
import { useSynapse } from 'app/lib/synaptik';
import { OnboardingProvider } from 'app/components/auth/Onboarding';
import { SignUpPageWithRouter } from '../SignUpPageWithRouter/SignUpPageWithRouter';
import { Eligibility } from './Eligibility';
import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk';
import { DrugSelection } from 'app/components/auth/SignUp/SignUpWithEligibility/DrugSelection';
import {
  serviceDrugsOptions,
  ServiceDrugType,
} from 'app/components/auth/SignUp/SignUpWithEligibility/serviceDrugsOptions';

export type SignUpWithEligibilitySteps = 'eligibility' | 'drug' | 'signUp';

function SignUpWithEligibility({ optimizely }: WithOptimizelyProps) {
  const [step, setStep] = useState<SignUpWithEligibilitySteps>('eligibility');
  const [optimizelyEdDrug, setOptimizelyEdDrug] = useState<boolean>();
  const [optimizelyBcDrug, setOptimizelyBcDrug] = useState<boolean>();
  const [optimizelyRemoveDob, setOptimizelyRemoveDob] = useState<boolean>();
  const [selectedDrug, setSelectedDrug] = useState<string>();
  const [service] = useSynapse(({ services }) => [services.getIntendedService()]);

  useEffect(() => {
    if (service?.code === 'ED') {
      setOptimizelyEdDrug(optimizely?.activate('ED_DRUG_SELECTION_PRE_REG') === 'drug_selection');
    }
    if (service?.code === 'BIRTH_CONTROL') {
      setOptimizelyBcDrug(optimizely?.activate('BC_DRUG_SELECTION_PRE_REG') === 'bc_drug_selection');
    }
    if (service?.code === 'UTI') {
      setOptimizelyRemoveDob(optimizely?.activate('REMOVING_DOB_FROM_UTI_ELIGIBILITY') === 'dob_removed');
    }
  }, [optimizely, service?.code]);

  const setNextStep = (currentStep: SignUpWithEligibilitySteps) => {
    if (currentStep === 'eligibility' && (optimizelyEdDrug || optimizelyBcDrug)) {
      setStep('drug');
    } else {
      setStep('signUp');
    }
  };

  const selectedVariant: ServiceDrugType =
    serviceDrugsOptions[service?.code] &&
    serviceDrugsOptions[service?.code].find((s: ServiceDrugType) => s.title === selectedDrug);

  return (
    <>
      {service && step === 'eligibility' && (
        <Eligibility
          nextStep={() => setNextStep('eligibility')}
          service={service}
          optimizelyRemoveDob={optimizelyRemoveDob}
        />
      )}
      {service && step === 'drug' && (
        <DrugSelection
          nextStep={() => setNextStep('drug')}
          serviceCode={service?.code}
          selectedDrug={selectedDrug}
          setSelectedDrug={setSelectedDrug}
        />
      )}
      {(!service || step === 'signUp') && (
        <SignUpPageWithRouter
          title={selectedVariant ? selectedVariant.createAccountTitle : !!service ? 'You’re Eligible!' : undefined}
          subtitle={selectedVariant ? selectedVariant.createAccountSubTitle : undefined}
          icon={selectedVariant ? selectedVariant.icon : undefined}
        />
      )}
    </>
  );
}

const SignUpWithEligibilityWrapper = withOptimizely((props: any) => {
  return (
    <OnboardingProvider>
      <SignUpWithEligibility {...props} />
    </OnboardingProvider>
  );
});

export { SignUpWithEligibilityWrapper as SignUpWithEligibility };
